import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { API_URL } from '../services/api';
import { Check, X, ArrowRight, Shield, Lock, Sparkles } from 'lucide-react';

const PLAN_DETAILS = {
  basic: {
    name: 'Basic',
    included: ['CRM (Sales, Marketing & Support)', 'AI Market Research (5 reports/mo)', 'MPO Engine — 5 of each per month (GTM strategies, emails, social, blogs, pitches)', '1 Team Member', 'Email Support'],
    excluded: ['Marketing Automation', 'Web Funnels', 'LMS & Courses'],
  },
  startup: {
    name: 'Startup',
    included: ['Everything in Basic', 'Marketing Automation', 'Unlimited MPO Engine', 'Surveys & Forms', '1 Web Funnel', '1 LMS Course', '5 Team Members'],
    excluded: [],
  },
  growth: {
    name: 'Growth',
    badge: 'MOST POPULAR',
    included: ['Everything in Startup', 'Unlimited Web Funnels', 'Unlimited LMS Courses', 'Unlimited Team Members', 'Priority Support'],
    excluded: [],
  },
  enterprise: {
    name: 'Enterprise',
    included: ['Everything in Growth', 'Dedicated Account Manager', 'Marketing Ops Team', 'Custom Integrations', 'Priority SLA'],
    excluded: [],
  },
};

const PricingPage = () => {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [plans, setPlans] = useState([]);
  const [inrRate, setInrRate] = useState(null);

  useEffect(() => {
    // Track affiliate referral
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    if (refCode) {
      document.cookie = `sqd_ref=${refCode};path=/;max-age=${90*24*60*60};SameSite=Lax`;
      localStorage.setItem('sqd_ref', refCode);
      localStorage.setItem('sqd_visitor_id', localStorage.getItem('sqd_visitor_id') || crypto.randomUUID());
      fetch(`${API_URL}/affiliate/track-visit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ref_code: refCode, visitor_id: localStorage.getItem('sqd_visitor_id') })
      }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    api.get(`${API_URL}/subscriptions/plans`).then(r => setPlans(r.data)).catch(() => {});
    api.get(`${API_URL}/payments/exchange-rate`).then(r => setInrRate(r.data.usd_inr)).catch(() => setInrRate(94));
  }, []);

  const getPrice = (plan) => billingCycle === 'annual' ? plan.annual_price : plan.monthly_price;

  return (
    <div className="min-h-screen bg-white">
      {/* Top bar */}
      <div className="bg-gray-900 text-white text-center py-2 text-sm">
        <span>All-in-one GTM platform — CRM, Automation, Funnels, LMS & more from </span>
        <span className="font-bold text-amber-400">$5/mo</span>
      </div>

      {/* Header */}
      <header className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
        <img src="/salesqualifyd-logo.png" alt="Salesqualifyd" className="h-9" onError={e => e.target.style.display = 'none'} />
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">Already a member?</span>
          <button onClick={() => navigate('/login')} className="text-sm font-semibold text-red-800 hover:underline">Login →</button>
        </div>
      </header>

      {/* Hero */}
      <section className="text-center py-12 px-4">
        <p className="text-sm font-semibold text-red-800 uppercase tracking-wide mb-3">Simple, Transparent Pricing</p>
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Plans Built for Growth at Every Stage</h1>
        <p className="text-gray-500 max-w-xl mx-auto">All-in-one GTM platform — market research, automation, CRM, funnels and more. Start small, scale up.</p>

        {/* Billing Toggle */}
        <div className="flex justify-center mt-8 mb-2">
          <div className="bg-gray-100 rounded-full p-1 flex" data-testid="pricing-billing-toggle">
            <button onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${billingCycle === 'monthly' ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}>
              Monthly
            </button>
            <button onClick={() => setBillingCycle('annual')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${billingCycle === 'annual' ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}>
              Annual
            </button>
          </div>
        </div>
        <div className="flex justify-center gap-4 text-xs text-gray-400">
          <span>✓ 72-hour money-back guarantee</span>
          <span>✓ Cancel any time</span>
          <span>✓ Instant access</span>
        </div>
      </section>

      {/* Plan Cards */}
      <section className="max-w-6xl mx-auto px-4 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          {['basic', 'startup', 'growth', 'enterprise'].map(pid => {
            const plan = plans.find(p => p.id === pid) || {};
            const details = PLAN_DETAILS[pid];
            const isGrowth = pid === 'growth';
            const isEnterprise = pid === 'enterprise';
            const price = getPrice(plan);

            return (
              <div key={pid} data-testid={`pricing-card-${pid}`}
                className={`rounded-xl border-2 p-6 flex flex-col ${isGrowth ? 'border-red-800 relative shadow-lg' : 'border-gray-200'}`}>
                {isGrowth && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-red-800 text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase">
                    Most Popular
                  </div>
                )}
                <h3 className={`text-lg font-bold ${isGrowth ? 'text-red-800' : 'text-gray-900'}`}>{details.name}</h3>

                {isEnterprise ? (
                  <div className="my-4">
                    <span className="text-3xl font-bold text-gray-900">Custom</span>
                    <p className="text-xs text-gray-500 mt-1">Contact us for pricing</p>
                  </div>
                ) : (
                  <div className="my-4">
                    <span className="text-3xl font-bold text-gray-900">${price}</span>
                    <span className="text-gray-500 text-sm">/{billingCycle === 'annual' ? 'yr' : 'mo'}</span>
                    {billingCycle === 'monthly' && plan.annual_price && (
                      <p className="text-xs text-gray-400 mt-1">or ${plan.annual_price}/yr</p>
                    )}
                    {inrRate && !isEnterprise && (
                      <p className="text-[10px] text-gray-400">≈ ₹{Math.round(price * inrRate).toLocaleString()} INR</p>
                    )}
                  </div>
                )}

                <ul className="space-y-2 flex-1 mb-5">
                  {details.included.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isGrowth ? 'text-red-800' : 'text-green-500'}`} />
                      {f}
                    </li>
                  ))}
                  {details.excluded.map((f, i) => (
                    <li key={`ex-${i}`} className="flex items-start gap-2 text-sm text-gray-400 line-through">
                      <X className="w-4 h-4 flex-shrink-0 mt-0.5 text-gray-300" />
                      {f}
                    </li>
                  ))}
                </ul>

                {isEnterprise ? (
                  <a href="mailto:support@salesqualifyd.com?subject=Enterprise%20Inquiry"
                    className="block text-center py-2.5 rounded-lg bg-gray-800 text-white font-semibold text-sm hover:bg-gray-900 transition-all">
                    Contact Sales
                  </a>
                ) : (
                  <button onClick={() => navigate(`/payment?plan=${pid}&cycle=${billingCycle}`)}
                    data-testid={`pricing-cta-${pid}`}
                    className={`w-full py-2.5 rounded-lg font-semibold text-sm transition-all ${
                      isGrowth ? 'bg-red-800 text-white hover:bg-red-900' : 'bg-gray-900 text-white hover:bg-gray-800'
                    }`}>
                    Get Started <ArrowRight className="w-4 h-4 inline ml-1" />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-center text-sm text-gray-400 mt-8 italic">
          Early customers with lifetime access are grandfathered — your access is unchanged, forever.
        </p>
      </section>

      {/* Footer */}
      <footer className="border-t py-6 text-center text-xs text-gray-400">
        <div className="flex justify-center gap-4 mb-2">
          <a href="/privacy-policy" className="hover:underline">Privacy</a>
          <a href="/terms-of-use" className="hover:underline">Terms</a>
          <a href="/refund-policy" className="hover:underline">Refund Policy</a>
          <a href="mailto:support@salesqualifyd.com" className="hover:underline">Contact</a>
        </div>
        <p>© {new Date().getFullYear()} Salesqualifyd. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default PricingPage;
